/**
 * Handles accordion placement below the LPO (Live Product Options) gallery.
 *
 * When the LPO app replaces the Liquid-rendered `.product-information__media` content
 * with its own `.cl-po--gallery-wrapper`, the `.product-information__media-accordion-slot`
 * is removed. This script recreates it as a sibling AFTER the LPO gallery wrapper
 * — without touching or moving the gallery element itself — so that
 * `.accordion--below-gallery` blocks can be placed under the images.
 *
 * IMPORTANT: we do NOT wrap or reparent `.cl-po--gallery-wrapper` because doing so
 * triggers LPO's internal MutationObserver and causes the gallery to reset to slide 1
 * whenever the variant changes.
 */
(function () {
  const SECTION_SELECTOR = '.product-information';
  const SLOT_SELECTOR = '.product-information__media-accordion-slot';
  const MEDIA_COL_SELECTOR = '.product-information__media';
  const LPO_WRAPPER_SELECTOR = '.cl-po--gallery-wrapper';
  const ACCORDION_BELOW_GALLERY_SELECTOR = '.accordion--below-gallery';

  /**
   * @param {HTMLElement} sectionEl
   * @returns {HTMLElement | null}
   */
  function ensureAccordionSlot(sectionEl) {
    const existingSlot = sectionEl.querySelector(SLOT_SELECTOR);
    if (existingSlot instanceof HTMLElement) return existingSlot;

    const mediaCol = sectionEl.querySelector(MEDIA_COL_SELECTOR);
    if (!(mediaCol instanceof HTMLElement)) return null;

    const lpoWrapper = mediaCol.querySelector(LPO_WRAPPER_SELECTOR);
    if (!(lpoWrapper instanceof HTMLElement)) return null;

    const slot = document.createElement('div');
    slot.className = 'product-information__media-accordion-slot';
    lpoWrapper.insertAdjacentElement('afterend', slot);
    return slot;
  }

  /**
   * @param {HTMLElement} sectionEl
   * @param {HTMLElement} slot
   */
  function placeAccordions(sectionEl, slot) {
    const accordions = Array.from(
      sectionEl.querySelectorAll(ACCORDION_BELOW_GALLERY_SELECTOR)
    ).filter((el) => el instanceof HTMLElement && !el.closest(SLOT_SELECTOR));
    accordions.forEach((acc) => slot.appendChild(acc));
  }

  /** @returns {boolean} */
  function relocate() {
    const sectionEl = document.querySelector(SECTION_SELECTOR);
    if (!(sectionEl instanceof HTMLElement)) return false;

    const slot = ensureAccordionSlot(sectionEl);
    if (!slot) return false;

    // On mobile, product-accordion-layout.js places the accordion after the customizer.
    if (!window.matchMedia('(max-width: 749px)').matches) {
      placeAccordions(sectionEl, slot);
    }

    return true;
  }

  function run() {
    relocate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Retry at increasing intervals in case LPO loads slowly
  [100, 400, 1000, 2500].forEach((ms) => setTimeout(run, ms));

  document.addEventListener('shopify:section:load', run);

  const desktopMq = window.matchMedia('(min-width: 750px)');
  if (typeof desktopMq.addEventListener === 'function') {
    desktopMq.addEventListener('change', run);
  } else if (typeof desktopMq.addListener === 'function') {
    desktopMq.addListener(run);
  }

  // Watch for LPO replacing the media column so we can re-create the slot
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let debounceTimer;
  const obs = new MutationObserver(() => {
    if (debounceTimer !== undefined) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = undefined;
      if (relocate()) obs.disconnect();
    }, 50);
  });
  obs.observe(document.body, { childList: true, subtree: true });
  // Stop watching after 20s regardless
  setTimeout(() => obs.disconnect(), 20000);
})();
